import React, {
  useMemo,
} from "react";

import {
  FiCpu,
  FiPlus,
  FiTrash2,
  FiX,
} from "react-icons/fi";


/* =========================================================
   EMPTY ATTRIBUTE
========================================================= */

const EMPTY_ATTRIBUTE = {
  name: "",
  value: "",
};


/* =========================================================
   COMMON ATTRIBUTE PRESETS
========================================================= */

const ATTRIBUTE_PRESETS = [
  "Processor",
  "Generation",
  "RAM",
  "Storage",
  "Graphics",
  "Screen Size",
  "Resolution",
  "Battery",
  "Keyboard",
  "Ports",
  "Operating System",
  "Material",
  "Dimensions",
  "Weight",
  "Compatibility",
  "Color",
];


/* =========================================================
   NORMALIZE TEXT
========================================================= */

const normalizeText = (
  value
) =>
  String(
    value ??
      ""
  ).trim();


/* =========================================================
   COMPONENT
========================================================= */

const DynamicAttributes = ({
  attributes = [],

  onChange,

  disabled = false,

  maxAttributes = 30,
}) => {
  /* =======================================================
     SAFE ATTRIBUTES
  ======================================================= */

  const safeAttributes =
    useMemo(
      () =>
        Array.isArray(
          attributes
        )
          ? attributes
          : [],
      [
        attributes,
      ]
    );


  /* =======================================================
     UPDATE PARENT
  ======================================================= */

  const updateAttributes =
    (
      nextAttributes
    ) => {
      if (
        typeof onChange !==
        "function"
      ) {
        return;
      }


      onChange(
        nextAttributes
      );
    };


  /* =======================================================
     ADD EMPTY ATTRIBUTE
  ======================================================= */

  const handleAddAttribute =
    () => {
      if (
        disabled ||
        safeAttributes.length >=
          maxAttributes
      ) {
        return;
      }


      updateAttributes([
        ...safeAttributes,

        {
          ...EMPTY_ATTRIBUTE,
        },
      ]);
    };


  /* =======================================================
     ADD PRESET
  ======================================================= */

  const handleAddPreset =
    (
      presetName
    ) => {
      if (
        disabled ||
        safeAttributes.length >=
          maxAttributes
      ) {
        return;
      }


      const alreadyExists =
        safeAttributes.some(
          (
            attribute
          ) =>
            normalizeText(
              attribute?.name
            ).toLowerCase() ===
            String(
              presetName
            ).toLowerCase()
        );


      if (
        alreadyExists
      ) {
        return;
      }


      updateAttributes([
        ...safeAttributes,

        {
          name:
            presetName,

          value:
            "",
        },
      ]);
    };


  /* =======================================================
     CHANGE ATTRIBUTE
  ======================================================= */

  const handleAttributeChange =
    (
      index,
      field,
      value
    ) => {
      const nextAttributes =
        safeAttributes.map(
          (
            attribute,
            attributeIndex
          ) => {
            if (
              attributeIndex !==
              index
            ) {
              return attribute;
            }


            return {
              ...attribute,

              [field]:
                value,
            };
          }
        );


      updateAttributes(
        nextAttributes
      );
    };


  /* =======================================================
     REMOVE ATTRIBUTE
  ======================================================= */

  const handleRemoveAttribute =
    (
      index
    ) => {
      if (
        disabled
      ) {
        return;
      }


      updateAttributes(
        safeAttributes.filter(
          (
            _,
            attributeIndex
          ) =>
            attributeIndex !==
            index
        )
      );
    };


  /* =======================================================
     CLEAR ALL
  ======================================================= */

  const handleClearAll =
    () => {
      if (
        disabled ||
        safeAttributes.length ===
          0
      ) {
        return;
      }


      updateAttributes(
        []
      );
    };


  /* =======================================================
     AVAILABLE PRESETS

     Hide presets already added.
  ======================================================= */

  const availablePresets =
    useMemo(
      () => {
        const selectedNames =
          new Set(
            safeAttributes
              .map(
                (
                  attribute
                ) =>
                  normalizeText(
                    attribute
                      ?.name
                  ).toLowerCase()
              )
              .filter(
                Boolean
              )
          );


        return ATTRIBUTE_PRESETS.filter(
          (
            preset
          ) =>
            !selectedNames.has(
              preset.toLowerCase()
            )
        );
      },
      [
        safeAttributes,
      ]
    );


  /* =======================================================
     LIMIT
  ======================================================= */

  const limitReached =
    safeAttributes.length >=
    maxAttributes;


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-dynamic-attributes"
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <div
        className="inventory-dynamic-attributes-header"
      >
        <div>
          <div
            className="inventory-dynamic-attributes-title"
          >
            <span
              className="inventory-dynamic-attributes-title-icon"
            >
              <FiCpu />
            </span>


            <div>
              <strong>
                Product Attributes
              </strong>


              <span>
                Add category-specific product
                specifications such as processor,
                RAM, storage, screen size,
                material, dimensions or
                compatibility.
              </span>
            </div>
          </div>
        </div>


        <div
          className="inventory-dynamic-attributes-actions"
        >
          {safeAttributes.length >
          0 ? (
            <button
              type="button"
              className="inventory-dynamic-attributes-clear"
              onClick={
                handleClearAll
              }
              disabled={
                disabled
              }
            >
              <FiX />

              Clear All
            </button>
          ) : null}


          <button
            type="button"
            className="inventory-dynamic-attributes-add"
            onClick={
              handleAddAttribute
            }
            disabled={
              disabled ||
              limitReached
            }
          >
            <FiPlus />

            Add Attribute
          </button>
        </div>
      </div>


      {/* ===================================================
          QUICK PRESETS
      =================================================== */}

      {availablePresets.length >
        0 &&
      !limitReached ? (
        <div
          className="inventory-dynamic-attribute-presets"
        >
          <span
            className="inventory-dynamic-attribute-presets-label"
          >
            Quick Add:
          </span>


          <div
            className="inventory-dynamic-attribute-presets-list"
          >
            {availablePresets
              .slice(
                0,
                10
              )
              .map(
                (
                  preset
                ) => (
                  <button
                    key={
                      preset
                    }
                    type="button"
                    onClick={() =>
                      handleAddPreset(
                        preset
                      )
                    }
                    disabled={
                      disabled
                    }
                  >
                    <FiPlus />

                    {preset}
                  </button>
                )
              )}
          </div>
        </div>
      ) : null}


      {/* ===================================================
          EMPTY STATE
      =================================================== */}

      {safeAttributes.length ===
      0 ? (
        <div
          className="inventory-dynamic-attributes-empty"
        >
          <div
            className="inventory-dynamic-attributes-empty-icon"
          >
            <FiCpu />
          </div>


          <strong>
            No product attributes added
          </strong>


          <span>
            Add specifications manually or use
            one of the quick options above.
          </span>


          <button
            type="button"
            onClick={
              handleAddAttribute
            }
            disabled={
              disabled ||
              limitReached
            }
          >
            <FiPlus />

            Add First Attribute
          </button>
        </div>
      ) : (
        /* =================================================
           ATTRIBUTE LIST
        ================================================= */

        <div
          className="inventory-dynamic-attributes-list"
        >
          {/* ===============================================
              TABLE-LIKE HEADER
          =============================================== */}

          <div
            className="inventory-dynamic-attribute-table-header"
          >
            <span>
              Attribute
            </span>


            <span>
              Value
            </span>


            <span>
              Action
            </span>
          </div>


          {safeAttributes.map(
            (
              attribute,
              index
            ) => {
              const name =
                attribute?.name ||
                "";

              const value =
                attribute?.value ||
                "";


              return (
                <div
                  key={`dynamic-attribute-${index}`}
                  className="inventory-dynamic-attribute-row"
                >
                  {/* =======================================
                      ATTRIBUTE NAME
                  ======================================= */}

                  <div
                    className="inventory-dynamic-attribute-field"
                  >
                    <label>
                      Attribute Name
                    </label>


                    <input
                      type="text"
                      value={
                        name
                      }
                      onChange={(
                        event
                      ) =>
                        handleAttributeChange(
                          index,
                          "name",
                          event
                            .target
                            .value
                        )
                      }
                      placeholder="Example: Processor"
                      disabled={
                        disabled
                      }
                      maxLength={
                        100
                      }
                    />
                  </div>


                  {/* =======================================
                      ATTRIBUTE VALUE
                  ======================================= */}

                  <div
                    className="inventory-dynamic-attribute-field"
                  >
                    <label>
                      Value
                    </label>


                    <input
                      type="text"
                      value={
                        value
                      }
                      onChange={(
                        event
                      ) =>
                        handleAttributeChange(
                          index,
                          "value",
                          event
                            .target
                            .value
                        )
                      }
                      placeholder="Example: Intel Core i5 11th Gen"
                      disabled={
                        disabled
                      }
                      maxLength={
                        300
                      }
                    />
                  </div>


                  {/* =======================================
                      REMOVE
                  ======================================= */}

                  <div
                    className="inventory-dynamic-attribute-action"
                  >
                    <label>
                      &nbsp;
                    </label>


                    <button
                      type="button"
                      className="inventory-dynamic-attribute-remove"
                      onClick={() =>
                        handleRemoveAttribute(
                          index
                        )
                      }
                      disabled={
                        disabled
                      }
                      title="Remove attribute"
                      aria-label={`Remove attribute ${
                        index +
                        1
                      }`}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}


      {/* ===================================================
          EXAMPLES + FOOTER
      =================================================== */}

      <div
        className="inventory-dynamic-attributes-bottom"
      >
        <div
          className="inventory-dynamic-attributes-examples"
        >
          <strong>
            Examples:
          </strong>

          <span>
            Processor, Generation, RAM,
            Storage, Graphics, Screen Size,
            Resolution, Battery, Keyboard,
            Ports, Material, Compatibility.
          </span>
        </div>


        <div
          className="inventory-dynamic-attributes-footer"
        >
          <span
            className={
              limitReached
                ? "limit-reached"
                : ""
            }
          >
            {safeAttributes.length}
            {" / "}
            {maxAttributes}
            {" attributes"}
          </span>
        </div>
      </div>
    </div>
  );
};


export default DynamicAttributes;